import { Box, Card, CardContent, Divider, Grid, Typography } from '@mui/material';
import {
  BooleanInput,
  Edit,
  FormDataConsumer,
  FormTab,
  Labeled,
  NumberInput,
  ReferenceArrayInput,
  SaveButton,
  SelectArrayInput,
  TabbedForm,
  TextInput,
  Toolbar,
  maxValue,
  minValue,
  useGetOne,
  useNotify,
  usePermissions,
  useTranslate
} from 'react-admin';
import { Tooltip, Wrapper } from '../../components';
import { HexColorInput, InternationalPhoneInput } from '../../components/Inputs';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { EVENTS, track } from '../../lib/analytics';
import { appLabelsChanged } from '../../store/providers/appConfig';
import AvailableOfferOnMobile from '../AppLabels/availableOfferOnMobile';
import StoreVersionPanel from '../chains/StoreVersionPanel';
import HomeItemsInput from '../settings/HomeItemsInput';
import CarouselCardsInput from '../settings/HomeItemsInput/CarouselCardsInput';
import HomePreview from '../settings/HomeItemsInput/HomePreview';
import { FirebaseMissingAlert, FirebaseOnly, orderCards } from '../settings/firebaseConfig';
import AppDownloadQRCode from './AppDownloadQRCode';
import FloatingPreview from './FloatingPreview';
import { APP_CONFIG_RESOURCE, APP_CONFIG_TAB_PATHS } from './tabs';

const sectionTitleSx = { mt: 2, mb: 1, fontWeight: 'bold' };

// A coluna dos campos tem largura própria para o mockup ficar colado nela, e não
// empurrado para a borda da tela.
const PREVIEW_WIDTH = 300;
const WITH_PREVIEW_SX = { display: 'flex', alignItems: 'flex-start', gap: 4, width: '100%' };
const FORM_COLUMN_SX = { flex: '0 1 760px', minWidth: 0 };

const PontuaxOnlyHint = () => (
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
    Só a equipe PontuaX vê estas opções.
  </Typography>
);

const AppConfigToolbar = props => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

export default () => {
  const notify = useNotify();
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const isAdmin = !!permissions?.includes(PERMISSIONS.ADMIN);
  // As flags de módulo moram no cadastro da rede: o editor da Home e o destino dos
  // cards só oferecem o que a rede contratou.
  const { data: chain } = useGetOne('chains', { id: getChainId() }, { enabled: !!getChainId() });


  const onSuccess = (_data, variables) => {
    if (appLabelsChanged(variables?.data, variables?.previousData)) {
      track(EVENTS.APP_LABEL_EDITED, { chain_id: getChainId() ?? 'unknown' });
    }
    notify('ra.notification.updated', { messageArgs: { smart_count: 1 } });
  };

  const onError = (error) => {
    notify(error?.message || 'ra.notification.data_provider_error', { type: 'error' });
  };

  return (
    <Permission permission={PERMISSIONS.SETTINGS}>
      {
        getChainId() ?
          <Edit
            id={getChainId()}
            resource={APP_CONFIG_RESOURCE}
            title="Aplicativo"
            mutationMode='pessimistic'
            mutationOptions={{ onSuccess, onError }}
            transform={orderCards}
          >
            <TabbedForm toolbar={<AppConfigToolbar />}>
              <FormTab label="Home">
                <Box sx={WITH_PREVIEW_SX}>
                  <Box sx={FORM_COLUMN_SX}>
                    <FirebaseMissingAlert />
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Topo da Home</Typography>
                    <FirebaseOnly>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showCashbackAtHome')}>
                        <BooleanInput label="Saldo em Cashback na Home do app" source="firebaseConfig.show_cashback_at_home" defaultValue={false} />
                      </Tooltip>
                    </FirebaseOnly>
                    <Permission permission={PERMISSIONS.APP_LABEL}>
                      <Box sx={{ maxWidth: 760 }}>
                        <TextInput source="appLabel.pointsBalanceLabel" label="Label do saldo de pontos" helperText='Em branco, o app mostra "Saldo em Pontos".' fullWidth />
                        <TextInput source="appLabel.cashbackBalanceLabel" label="Label do saldo de cashback" helperText='Em branco, o app mostra "Saldo Cashback".' fullWidth />
                      </Box>
                    </Permission>
                    <Divider sx={{ width: '100%', my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Seções da Home</Typography>
                    <HomeItemsInput chain={chain} />
                    <FirebaseOnly>
                      <>
                        <Divider sx={{ width: '100%', my: 2 }} />
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Imagens do carrossel</Typography>
                        <CarouselCardsInput chain={chain} />
                      </>
                    </FirebaseOnly>
                  </Box>
                  <FloatingPreview width={PREVIEW_WIDTH}>
                    <HomePreview />
                  </FloatingPreview>
                </Box>
              </FormTab>

              <FormTab label="Abastecimento" path={APP_CONFIG_TAB_PATHS.fillin}>
                <Labeled label="Na hora de abastecer">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useFillinPreset')}>
                      <BooleanInput label="Usar preset no abastecimento" source="useFillinPreset" />
                    </Tooltip>
                    <FirebaseOnly>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.discountButtonEnabled')}>
                        <BooleanInput label="Botão de desconto ativado por padrão" source="firebaseConfig.discount_button_enabled" defaultValue />
                      </Tooltip>
                    </FirebaseOnly>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.appAutoExpandDiscountModal')}>
                      <BooleanInput label="Forçar exibição dos descontos no abastecimento" source="appAutoExpandDiscountModal" defaultValue />
                    </Tooltip>
                  </>
                </Labeled>
                <Permission permission={PERMISSIONS.ADMIN}>
                  <Labeled label="Regras do abastecimento no app">
                    <>
                      <PontuaxOnlyHint />
                      <BooleanInput label="Somente encher tanque" source="appOnlyCompletelyFillin" />
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title={translate('tooltips.useVehicleOdometer')}>
                        <BooleanInput label="Usar odômetro de veículo" source="useVehicleOdometer" />
                      </Tooltip>
                    </>
                  </Labeled>
                </Permission>
                <Labeled label="Tela de detalhes do abastecimento">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showFillinMsgPerLiterDiscount')}>
                      <BooleanInput label="Mostrar desconto por litro" source="showFillinMsgPerLiterDiscount" />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showFillinMsgPoints')}>
                      <BooleanInput label="Mostrar Pontuação" source="showFillinMsgPoints" />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showFillinMsgCashback')}>
                      <BooleanInput label="Mostrar cashback" source="showFillinMsgCashback" />
                    </Tooltip>
                  </>
                </Labeled>
              </FormTab>

              <FormTab label="Cupons e descontos" path={APP_CONFIG_TAB_PATHS.coupons}>
                <Box sx={WITH_PREVIEW_SX}>
                  <Box sx={FORM_COLUMN_SX}>
                    <Labeled label="Cupons e Ofertas">
                      <>
                        <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.isOffersEnabledInCoupon')}>
                          <BooleanInput label="Mostrar ofertas na tela de cupons" source="isOffersEnabledInCoupon" />
                        </Tooltip>
                        <FormDataConsumer>
                          {({ formData }) => (
                            formData.isOffersEnabledInCoupon &&
                            <Card variant="outlined" sx={{ backgroundColor: 'grey.100', marginBottom: 2 }}>
                              <CardContent>
                                <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 14, marginBottom: 2 }}>
                                  Configurações da aba de ofertas
                                </Typography>
                                <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.showPricesInApp')}>
                                  <BooleanInput label="Mostrar preço final em vez do desconto (combustível)" source="showPricesInApp" />
                                </Tooltip>
                                {
                                  formData.showPricesInApp &&
                                  <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title={translate('tooltips.priceVisibleGroupIds')}>
                                    <ReferenceArrayInput
                                      reference="chains/chainId/customer-groups"
                                      source="priceVisibleGroupIds"
                                      perPage={null}
                                    >
                                      <SelectArrayInput optionText="name" label="Exibir preço final apenas para grupos" style={{ minWidth: 350, marginBottom: 0, paddingBottom: 0 }} />
                                    </ReferenceArrayInput>
                                  </Tooltip>
                                }
                              </CardContent>
                            </Card>
                          )}
                        </FormDataConsumer>
                      </>
                    </Labeled>
                    <Permission permission={PERMISSIONS.APP_LABEL}>
                      <Box sx={{ maxWidth: 760 }}>
                        <Typography variant="subtitle1" sx={sectionTitleSx}>Textos da tela de Descontos</Typography>
                        <TextInput source="appLabel.discountTitleLabel" label="Titulo" fullWidth />
                        <TextInput source="appLabel.discountSubtitleLabel" label="Descrição" fullWidth />
                        <TextInput source="appLabel.discountDisclaimerLabel" label="Descrição da regra" fullWidth />
                        <Typography variant="subtitle1" sx={sectionTitleSx}>Tela Resgatar</Typography>
                        <TextInput source="appLabel.cashProductsSectionLabel" label="Título da seção de produtos em dinheiro" helperText='Em branco, o app mostra "Vales".' fullWidth />
                      </Box>
                    </Permission>
                  </Box>
                  <Permission permission={PERMISSIONS.APP_LABEL}>
                    <FloatingPreview width={PREVIEW_WIDTH}>
                      <FormDataConsumer>
                        {({ formData }) => (
                          <AvailableOfferOnMobile data={formData} />
                        )}
                      </FormDataConsumer>
                    </FloatingPreview>
                  </Permission>
                </Box>
              </FormTab>

              <FormTab label="Avaliação e avisos" path={APP_CONFIG_TAB_PATHS.rating}>
                <Labeled label="Avaliação">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.requireRatingOnFillin')}>
                      <BooleanInput label="Obrigar usuário a avaliar abastecimento" source="requireRatingOnFillin" />
                    </Tooltip>
                    <FirebaseOnly>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.shouldShowStationRating')}>
                        <BooleanInput label="Mostrar avaliação do posto" source="firebaseConfig.should_show_station_rating" defaultValue={false} />
                      </Tooltip>
                    </FirebaseOnly>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.appStoreReviewEnabled')}>
                      <BooleanInput label="Solicitar avaliação nas lojas (App Store/Play Store)" source="appStoreReviewEnabled" defaultValue={false} />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Notificações">
                  <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.appForceNotificationPermission')}>
                    <BooleanInput label="Insistir permissão de notificação" source="appForceNotificationPermission" defaultValue={false} />
                  </Tooltip>
                </Labeled>
                <Labeled label="Promoter">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.allowPromoterUnlinking')}>
                      <BooleanInput label="Permitir desassociação de Promoter" source="allowPromoterUnlinking" />
                    </Tooltip>
                  </>
                </Labeled>
                <FirebaseOnly>
                  <Labeled label="Sorteio">
                    <>
                      <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title={translate('tooltips.raffleRegulationUrl')}>
                        <TextInput label="Regulamento do Número da Sorte" source="firebaseConfig.raffle_regulation_url" style={{ minWidth: 350 }} />
                      </Tooltip>
                    </>
                  </Labeled>
                </FirebaseOnly>
              </FormTab>

              <FormTab label="Cadastro e login" path={APP_CONFIG_TAB_PATHS.signup}>
                <Box display={{ xs: 'block', sm: 'flex' }}>
                  <Box flex={2} mr={{ xs: 0, sm: '1.5em' }}>
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title={translate('tooltips.maxDevicePerCpf')}>
                      <NumberInput
                        label="Máximo de dispositivos por CPF"
                        source="maxDevicePerCpf"
                        min={1}
                        validate={[minValue(1)]}
                        parse={(value) => (isNaN(value) || value <= 0 ? null : Math.round(value))}
                        style={{ minWidth: 300 }}
                      />
                    </Tooltip>
                  </Box>
                  <Box flex={2}>
                    <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title={translate('tooltips.maxCpfPerDevice')}>
                      <NumberInput
                        label="Máximo de CPFs por dispositivo"
                        source="maxCpfPerDevice"
                        min={1}
                        validate={[minValue(1)]}
                        parse={(value) => (isNaN(value) || value <= 0 ? null : Math.round(value))}
                        style={{ minWidth: 300 }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
                <Permission permission={PERMISSIONS.ADMIN}>
                  <Labeled label="Verificação do cadastro">
                    <>
                      <PontuaxOnlyHint />
                      <BooleanInput label="Enviar código de verificação por SMS/WhatsApp" source="hasValidationTokenByMessage" />
                      <BooleanInput label="Usar QR Code na verificação de SignUp" source="appUseQrCodeForSignup" />
                      <BooleanInput source="useCode2fa" label="Habilitar código temporário no app" />
                    </>
                  </Labeled>
                </Permission>
              </FormTab>

              {isAdmin &&
                <FormTab label="Identidade visual" path={APP_CONFIG_TAB_PATHS.identity}>
                  <PontuaxOnlyHint />
                  <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Typography>Dados</Typography>
                      <Box sx={{ display: { xs: 'block', sm: 'flex' } }}>
                        <TextInput style={{ marginRight: 12 }} source="appName" label="Nome Ícone (Padrão)" variant="standard" fullWidth />
                        <TextInput style={{ marginRight: 12 }} source="appNameInsideApp" label="Nome Interno" variant="standard" fullWidth />
                        <TextInput source="appNameStore" label="Nome na Loja" variant="standard" fullWidth />
                      </Box>
                      <Box sx={{ display: { xs: 'block', sm: 'flex' } }}>
                        <TextInput style={{ marginRight: 12 }} source="appSlogan" label="Slogan" variant="standard" fullWidth />
                        <InternationalPhoneInput source="appPhone" label="Telefone" variant="standard" fullWidth />
                      </Box>
                      <Typography>Layout</Typography>
                      <Box sx={{ display: { xs: 'block', sm: 'flex' } }}>
                        <HexColorInput style={{ marginRight: 12 }} source="appColor" label="Cor principal" fullWidth />
                        <HexColorInput source="appHeaderLabelColor" label="Cor da fonte na Home" fullWidth />
                      </Box>
                      <Box sx={{ display: { xs: 'block', sm: 'flex' } }}>
                        <HexColorInput style={{ marginRight: 12 }} source="appSignUpButtonColor" label="Cor do botão de Cadastro" fullWidth />
                        <HexColorInput style={{ marginRight: 12 }} source="appSignInButtonColor" label="Cor do botão de Login" fullWidth />
                        <NumberInput source="appLogoWidth" label="Largura da logo na tela Inicial" validate={[minValue(0), maxValue(100)]} fullWidth />
                      </Box>
                      <BooleanInput source="appShowWelcomeLabel" label="Mostrar mensagem Bem Vindo" />
                      <Divider sx={{ width: '100%', my: 1.5 }} />
                      <Typography>Sociais</Typography>
                      <Box sx={{ display: { xs: 'block', sm: 'flex' } }}>
                        <TextInput style={{ marginRight: 12 }} source="appInstagram" label="Instagram" variant="standard" fullWidth />
                        <TextInput style={{ marginRight: 12 }} source="appTwitter" label="Twitter" variant="standard" fullWidth />
                        <TextInput source="appYoutube" label="Youtube" variant="standard" fullWidth />
                      </Box>
                      <Box sx={{ display: { xs: 'block', sm: 'flex' } }}>
                        <TextInput style={{ marginRight: 12 }} source="appFacebookPageId" label="Facebook - Id da página" variant="standard" fullWidth />
                        <TextInput source="appFacebook" label="Facebook" variant="standard" fullWidth />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }} sx={{ alignSelf: 'center' }}>
                      <AppDownloadQRCode />
                    </Grid>
                  </Grid>
                </FormTab>
              }

              {isAdmin &&
                <FormTab label="Lojas e atualização" path={APP_CONFIG_TAB_PATHS.stores}>
                  <PontuaxOnlyHint />
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Identificação nas Lojas</Typography>
                  <TextInput source="firebaseProjectId" label="Id do Projeto no Firebase" InputProps={{ autoComplete: "do-not-autofill" }} sx={{ width: 350 }} fullWidth={false} />
                  <TextInput source="googlePackageName" label="Nome do Pacote no Google" InputProps={{ autoComplete: "do-not-autofill" }} sx={{ width: 380 }} fullWidth={false} />
                  <TextInput source="applePackageName" label="Nome do Pacote na Apple" InputProps={{ autoComplete: 'do-not-autofill' }} sx={{ width: 380 }} fullWidth={false} />
                  <TextInput source="appleAppId" label="ID do Aplicativo na Apple" InputProps={{ id: "field1", autoComplete: "cc-exp" }} sx={{ width: 280 }} fullWidth={false} />
                  <StoreVersionPanel />
                  <Divider sx={{ width: '100%', my: 1.5 }} />
                  <Typography variant="subtitle1" sx={sectionTitleSx}>Atualização Forçada do App</Typography>
                  <Box sx={{ backgroundColor: '#fff3e0', p: 2, borderRadius: 1, mb: 2, border: '1px solid #ffcc80' }}>
                    <Typography variant="body2" color="warning.dark">
                      ⚠️ <strong>Atenção:</strong> Configurar o build mínimo irá impactar imediatamente todos os usuários do app que estiverem com uma versão inferior. Eles verão uma tela solicitando atualização.
                    </Typography>
                  </Box>
                  <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Número do build mínimo exigido para usar o app. Usuários com build inferior verão uma tela de atualização.">
                    <NumberInput source="appMinBuild" label="Build Mínimo do App" />
                  </Tooltip>
                  <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando marcado, o usuário NÃO poderá dispensar a tela de atualização e será obrigado a atualizar o app. Quando desmarcado, o usuário pode escolher 'Deixar pra depois'.">
                    <BooleanInput label="Forçar atualização (bloquear app)" source="appForceUpdate" />
                  </Tooltip>
                </FormTab>
              }
            </TabbedForm>
          </Edit>
          :
          <Wrapper>
            <Typography>Selecione uma rede para fazer alterações</Typography>
          </Wrapper>
      }
    </Permission>
  );
};
